'use client';

import * as React from 'react';
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import { cn } from '@savvy-kit/lib/utils';

function TooltipProvider({ delay = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />;
}

type TooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root> & {
  /** How long to wait before opening the tooltip, in ms. Forwarded to the wrapping `TooltipProvider`. */
  readonly delay?: number;
};

function Tooltip({ delay, ...props }: TooltipProps) {
  return (
    <TooltipProvider delay={delay}>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

type TooltipContentProps = React.ComponentProps<typeof TooltipPrimitive.Popup> &
  Pick<React.ComponentProps<typeof TooltipPrimitive.Positioner>, 'side' | 'sideOffset' | 'align' | 'alignOffset'>;

function TooltipContent({
  className,
  side,
  sideOffset = 8,
  align,
  alignOffset,
  children,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className="z-[1400]"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            'bg-foreground text-white dark:[background-color:var(--elevation-3)] dark:[color:var(--foreground-light)] data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--transform-origin) rounded-md px-3 py-1.5 text-xs text-balance max-w-[256px]',
            className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow
            className={cn(
              'bg-foreground fill-foreground dark:[background-color:var(--elevation-3)] dark:[fill:var(--elevation-3)] z-50 size-2.5 rotate-45 rounded-xs',
              // base-ui only sets the cross-axis position on the Arrow; anchor the
              // main axis ourselves so the arrow lands at the correct popup edge.
              'data-[side=top]:bottom-0 data-[side=top]:translate-y-[calc(50%_-_2px)]',
              'data-[side=bottom]:top-0 data-[side=bottom]:translate-y-[calc(-50%_+_2px)]',
              'data-[side=left]:right-0 data-[side=left]:translate-x-[calc(50%_-_2px)]',
              'data-[side=right]:left-0 data-[side=right]:translate-x-[calc(-50%_+_2px)]',
            )}
          />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
